import styled from 'styled-components';
import { Link } from 'react-router-dom';

import { tokens, media } from '../../../design/tokens';

interface RowProps {
  $first?: boolean;
  $revealed?: boolean;
}

/**
 * Fileira de categoria.
 *
 * Não tem mais altura fixa. Antes era `height: 250px` com `max-height`
 * casada, o que recortava justamente o card crescido no hover — o gesto
 * assinatura do sistema era cortado pela própria fileira. Agora a altura vem
 * da proporção da capa mais a faixa de metadados.
 */
export const Container = styled.section<RowProps>`
  position: relative;
  z-index: ${tokens.zIndex.row};

  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 100%;

  margin-top: ${(props) => (props.$first ? tokens.row.gapFirst : tokens.row.gap)};

  /* Revelação ao rolar. O padrão é VISÍVEL: o hook useInView só assume o
     estado escondido depois de confirmar que existe um IntersectionObserver
     para desfazê-lo. Sem JS, sem observer ou com movimento reduzido, a
     fileira nasce visível e assim permanece.

     Uma vez revelada, volta a transform: none — um translate residual
     mantém a fileira como stacking context e prende o card que cresce. */
  opacity: ${(props) => (props.$revealed === false ? 0 : 1)};
  transform: ${(props) =>
    props.$revealed === false ? 'translateY(16px)' : 'none'};
  transition: opacity ${tokens.motion.reveal} ease,
    transform ${tokens.motion.reveal} ${tokens.motion.easeOut};

  /* Enquanto o ponteiro (ou o foco) está nesta fileira, ela sobe acima das
     vizinhas — sem isto o card crescido fica por baixo da fileira seguinte. */
  &:hover,
  &:focus-within {
    z-index: ${tokens.zIndex.rowHover};
  }

  /* O bloco global de prefers-reduced-motion apenas encurta a transição para
     0.15s; quem cancela o deslocamento é esta regra. */
  @media (prefers-reduced-motion: reduce) {
    opacity: 1;
    transform: none;
    transition: none;
  }
`;

export const CategoryTitle = styled.div`
  display: flex;
  align-items: baseline;

  width: 100%;
  padding: 0 ${tokens.space.gutter};
  margin-bottom: ${tokens.row.headerGap};

  color: ${tokens.color.textMist};

  h3 {
    font-family: ${tokens.font.body.family};
    font-size: 1.125rem;
    font-weight: 600;
    letter-spacing: -0.01em;

    ${media.from(tokens.breakpoint.lg)} {
      font-size: 1.25rem;
    }
  }

  /* A contagem é metadado: recua um passo, não compete com o título. */
  p {
    margin-left: ${tokens.space.sm};

    font-family: ${tokens.font.caption.family};
    font-size: ${tokens.font.caption.size};
    font-weight: ${tokens.font.caption.weight};
    color: ${tokens.color.textAsh};
  }

  .category-title {
    width: 160px;
    height: 14px;
  }
`;

export const SeeAllItems = styled(Link)`
  margin-left: auto;
  text-decoration: none;

  display: flex;
  align-items: center;

  font-family: ${tokens.font.label.family};
  font-size: ${tokens.font.label.size};
  font-weight: ${tokens.font.label.weight};
  color: ${tokens.color.marqueeGold};

  > svg {
    font-size: 16px;
  }
`;
